import { useState, useMemo } from 'react';
import { computeWhatIf } from '../utils/computeWhatIf';
import { fmtCurrencyWhole } from '@/shared/utils/formatCurrency';

const REDUCTION_OPTS = [25, 50, 75, 100];

export default function WhatIfCalculator({ allTxns, selMonth, selYear, incomeList, expenseMap, goals, currency, theme: t }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [reductionPct, setReductionPct] = useState(50);

  const leafCategories = useMemo(
    () => Object.values(expenseMap || {}).flat().sort(),
    [expenseMap]
  );

  const result = useMemo(() => {
    if (!category) return null;
    return computeWhatIf(allTxns, category, reductionPct, selMonth, selYear, incomeList, goals);
  }, [allTxns, category, reductionPct, selMonth, selYear, incomeList, goals]);

  return (
    <div style={{
      background: t.cardBg,
      border: '1px solid ' + t.cardBorder,
      borderRadius: 14,
      padding: 16,
      marginBottom: 20,
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          width: '100%', padding: 0,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>
          {'🔮 What If Calculator'}
        </span>
        <span style={{ fontSize: 12, color: t.textMuted }}>{open ? '▲ Hide' : '▼ Show'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 12, color: t.textMuted }}>
            Simulate cutting a spending category and see the impact on your savings goals.
          </p>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: 'block', marginBottom: 6 }}>
              Category to reduce
            </label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: '1px solid ' + t.cardBorder,
                background: t.inputBg || t.cardBg, color: t.text, fontSize: 13,
              }}
            >
              <option value="">-- Pick a category --</option>
              {leafCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: 'block', marginBottom: 6 }}>
              Cut spending by
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {REDUCTION_OPTS.map(pct => (
                <button
                  key={pct}
                  onClick={() => setReductionPct(pct)}
                  style={{
                    flex: 1, padding: '8px 0', borderRadius: 8, border: 'none',
                    background: reductionPct === pct ? '#6366f1' : (t.inputBg || '#e2e8f0'),
                    color: reductionPct === pct ? 'white' : t.text,
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  {pct + '%'}
                </button>
              ))}
            </div>
          </div>

          {!category && (
            <p style={{ margin: 0, fontSize: 12, color: t.textMuted, textAlign: 'center', padding: '8px 0' }}>
              Pick a category above to see the impact.
            </p>
          )}

          {category && result === null && (
            <p style={{ margin: 0, fontSize: 12, color: t.textMuted, textAlign: 'center', padding: '8px 0' }}>
              {'Not enough spending history for ' + category + ' (need 2+ months of data).'}
            </p>
          )}

          {result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{
                background: 'rgba(99,102,241,0.08)',
                border: '1px solid rgba(99,102,241,0.2)',
                borderRadius: 12, padding: '14px 16px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Extra savings per month
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#6366f1' }}>
                  {'+' + fmtCurrencyWhole(result.monthlySavings, currency)}
                </div>
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
                  {'by cutting ' + category + ' ' + reductionPct + '% (avg ' + fmtCurrencyWhole(result.avg3mo, currency) + '/mo)'}
                </div>
              </div>

              {result.goalImpacts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>Impact on your savings goals</div>
                  {result.goalImpacts.map(impact => (
                    <div
                      key={impact.goalId}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 12px',
                        background: impact.monthsSaved > 0 ? 'rgba(22,163,74,0.07)' : (t.inputBg || '#f8fafc'),
                        border: '1px solid ' + (impact.monthsSaved > 0 ? 'rgba(22,163,74,0.2)' : t.cardBorder),
                        borderRadius: 10, gap: 12, flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.text }}>{impact.name}</div>
                      <div style={{ fontSize: 12, color: impact.monthsSaved > 0 ? '#16a34a' : t.textMuted, fontWeight: 700 }}>
                        {impact.monthsSaved > 0
                          ? impact.monthsSaved + ' month' + (impact.monthsSaved !== 1 ? 's' : '') + ' sooner'
                          : (impact.newMonths > 0
                            ? 'Reach in ~' + impact.newMonths + ' months'
                            : 'No contribution data')}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {result.goalImpacts.length === 0 && (
                <p style={{ margin: 0, fontSize: 12, color: t.textMuted, textAlign: 'center' }}>
                  No active savings goals to project. Add a goal in Savings.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
